<script setup lang="ts">
import ActivatorsAccordionFAQ from "@/views/Calculators/Activation/components/ActivatorsAccordionFAQ.vue";
import SectorsAccordionFAQ from "@/views/Calculators/Activation/components/SectorsAccordionFAQ.vue";
import { useRoute, useRouter } from "vue-router";
import { computed, onMounted, ref } from "vue";
import {
  ActivationGroup,
  ActivationItemGroup,
  ActivationsRequest,
} from "@/types/calculators";
import { activationGroups } from "@/constants/activations";
import { useI18n } from "vue-i18n";
import AccordionFaq from "@/components/accordion/AccordionFAQ.vue";
import { fetchActivations } from "@/api/calculators";
import DatePicker from "@/components/inputs/DatePicker.vue";
import CheckIcon from "@/components/icons/CheckIcon.vue";
import GradientButton from "@/components/buttons/GradientButton.vue";
import BackSidebar from "@/views/Calculators/Activation/components/BackSidebar.vue";
import { useSafeBack } from "@/utils/useSafeBack";
import { ROUTE_NAMES } from "@/constants/routeNames";
import { format } from "date-fns";
import ActivationResults from "@/views/Calculators/Activation/components/ActivationResults.vue";
import PageTitleWithBackButton from "@/components/PageTitleWithBackButton.vue";
import GooglePlaceAutocompleteInput from "@/components/inputs/GooglePlaceAutocompleteInput.vue";
import { useGoogleAutocomplete } from "@/utils/useGoogleAutocomplete";
import ArrowIcon from "@/components/icons/ArrowIcon.vue";
import PrintButton from "@/components/buttons/PrintButton.vue";
import { useVueToPrint } from "vue-to-print";
import { useToastNotificationStore } from "@/stores/toastNotificationStore";
import NotificationComponent from "@/components/notifications/NotificationComponent.vue";
import { PlaceSelectedPayload } from "@/types/google-places";
import { useFamilyStore } from "@/stores/familyStore";
import { useUserStore } from "@/stores/userStore";
import { PREMIUM_CALCULATORS, SUBSCRIPTION_TIERS } from "@/types/premiumTypes";
import AppDropdown from "@/components/inputs/AppDropdown.vue";
import ChevronIcon from "@/components/icons/ChevronIcon.vue";
import { FamilyMember } from "@/types/familyTypes";
import TextInput from "@/components/inputs/TextInput.vue";
import DownloadDocButton from "@/components/buttons/DownloadDocButton.vue";
import WordExporter from "@/views/Calculators/Activation/components/WordExporter.vue";
import AddOutlineButton from "@/components/buttons/AddOutlineButton.vue";

const route = useRoute();
const router = useRouter();
const { safeBack } = useSafeBack();
const { t } = useI18n();
const { normalizeGoogleTimeZone } = useGoogleAutocomplete("ru");
const toastStore = useToastNotificationStore();
const familyStore = useFamilyStore();
const { userHasSubscriptionWithTier } = useUserStore();

const printableRef = ref();
const isFormOpen = ref<boolean>(true);
const activationRequested = ref<boolean>(false);
const selectedActivations = ref([]);
const activationGroup = ref<ActivationGroup | null>(null);
const resultedActivations = ref<ActivationItemGroup>([]);
const birthday = ref<Date | null>(null);
const dateFrom = ref<Date | null>(null);
const dateTo = ref<Date | null>(null);
const latitude = ref<number | null>(null);
const longitude = ref<number | null>(null);
const timeZone = ref<string | null>(null);
const isLoading = ref<boolean>(false);
const isFamilyMembersDropdownOpen = ref(false);
const selectedFamilyMember = ref<FamilyMember>();

const isFieldsCorrect = computed(() => {
  let activations = activationGroup.value?.activationKeys ?? [];
  if (hasCheckboxes.value) {
    activations = selectedActivations.value;
  }

  if (
    userHasSubscriptionWithTier(
      PREMIUM_CALCULATORS.activations,
      SUBSCRIPTION_TIERS.consultant
    )
  ) {
    return (
      activations.length > 0 &&
      dateFrom.value &&
      dateTo.value &&
      // birthday.value &&
      latitude.value &&
      longitude.value &&
      timeZone.value
    );
  }

  if (
    userHasSubscriptionWithTier(
      PREMIUM_CALCULATORS.activations,
      SUBSCRIPTION_TIERS.family
    )
  ) {
    return (
      activations.length > 0 &&
      dateFrom.value &&
      dateTo.value &&
      selectedFamilyMember.value !== undefined
    );
  }

  return (
    activations.length > 0 &&
    dateFrom.value &&
    dateTo.value &&
    familyStore.familyMembers.length > 0
  );
});

const hasCheckboxes = computed(
  () => activationGroup.value && activationGroup.value.activationKeys.length > 1
);

const { handlePrint } = useVueToPrint({
  content: printableRef,
  documentTitle: "Activation Result",
  removeAfterPrint: true,
  bodyClass: "p-4",
});

onMounted(() => {
  const activation = route.query.activation?.toString() ?? null;
  activationGroup.value =
    activationGroups.find((g) => g.key === activation) ?? null;
  if (!activationGroup.value) {
    safeBack(ROUTE_NAMES.ACTIVATIONS);
  }
  loadFamilyMembers();
});

function loadFamilyMembers() {
  familyStore.loadIfNeeded().then(() => {
    if (familyStore.familyMembers.length > 0) {
      selectedFamilyMember.value = familyStore.familyMembers[0];
    }
  });
}

function changeActivation(newActivation: ActivationGroup) {
  activationGroup.value = newActivation;
  resultedActivations.value = [];
  selectedActivations.value = [];

  const newQuery = { ...route.query, activation: newActivation.key };
  router.replace({ name: route.name as string, query: newQuery });
}

function handlePlaceSelected(payload: PlaceSelectedPayload) {
  latitude.value = payload.location.lat();
  longitude.value = payload.location.lng();
  timeZone.value = normalizeGoogleTimeZone(payload.timeZone);
}

async function handleSubmit() {
  if (isLoading.value || !isFieldsCorrect.value) {
    return;
  }

  isLoading.value = true;
  try {
    resultedActivations.value = [];
    activationRequested.value = false;
    const activations = await fetchActivations(getActivationRequest());
    resultedActivations.value = activations.sort((a, b) => {
      const [dA, mA, yA] = a.date.split("-");
      const [dB, mB, yB] = b.date.split("-");
      const dateComparison = `${yA}-${mA}-${dA}`.localeCompare(
        `${yB}-${mB}-${dB}`
      );
      if (dateComparison !== 0) {
        return dateComparison;
      }

      const timeA = a.time_interval.split("-")[0];
      const timeB = b.time_interval.split("-")[0];

      return timeA.localeCompare(timeB);
    });
    activationRequested.value = true;
  } catch (e) {
    await toastStore.createNotificationsFromApi(e);
    console.log(e);
  } finally {
    isLoading.value = false;
  }
}

function getActivationRequest(): ActivationsRequest {
  let activations = activationGroup.value?.activationKeys ?? [];
  if (hasCheckboxes.value) {
    activations = selectedActivations.value;
  }
  let request = {
    activations: activations,
    dateFrom: format(dateFrom.value as Date, "yyyy-MM-dd"),
    dateTo: format(dateTo.value as Date, "yyyy-MM-dd"),
  };

  if (
    userHasSubscriptionWithTier(
      PREMIUM_CALCULATORS.activations,
      SUBSCRIPTION_TIERS.consultant
    )
  ) {
    return {
      ...request,
      type: "consultant",
      birthday: format(birthday.value as Date, "yyyy-MM-dd"),
      latitude: latitude.value as number,
      longitude: longitude.value as number,
      timezone: timeZone.value as string,
    };
  }

  if (
    userHasSubscriptionWithTier(
      PREMIUM_CALCULATORS.activations,
      SUBSCRIPTION_TIERS.family
    )
  ) {
    return {
      ...request,
      type: "family-member",
      familyMemberId: selectedFamilyMember.value?.id as number,
    };
  }

  return {
    ...request,
    type: "individual",
  };
}

function toggleForm() {
  if (resultedActivations.value.length == 0) {
    return;
  }
  isFormOpen.value = !isFormOpen.value;
}

function handleBack() {
  safeBack(ROUTE_NAMES.HOME);
}

function selectFamilyMember(familyMember: FamilyMember) {
  selectedFamilyMember.value = familyMember;
  isFamilyMembersDropdownOpen.value = false;
}

function handleOpenAddFamilyMemberModal() {
  router.replace({
    name: ROUTE_NAMES.USER,
    query: { openFamilyModal: "true" },
  });
}
</script>

<template>
  <div class="flex w-full h-full max-md:overflow-y-auto no-scrollbar">
    <!-- Main Content -->
    <div class="flex-1 mx-auto pl-1 mt-6 max-md:px-4 md:overflow-y-auto">
      <PageTitleWithBackButton
        v-if="activationGroup?.key !== undefined"
        :page-title="t(`calculators.activations.group.${activationGroup?.key}`)"
        :title-align="'left'"
      />
      <!-- Form -->
      <div class="bg-white border border-ternary rounded-[20px] mb-8">
        <button
          :class="[
            'w-full flex items-center px-4 lg:px-10',
            isFormOpen ? 'mb-4 lg:mb-10 pt-6' : 'py-2 lg:py-6',
          ]"
          @click="toggleForm"
        >
          <span
            class="size-10 shrink-0 mr-4 hidden xl:flex items-center justify-center rounded-full bg-brown hover:bg-active"
            @click="handleBack"
          >
            <ArrowIcon class="text-white -rotate-[135deg]" />
          </span>
          <span
            v-if="activationGroup"
            class="text-left font-medium text-[32px] leading-[32px] lg:text-[40px] lg:leading-100p text-brown"
          >
            {{ t(`calculators.activations.group.${activationGroup?.key}`) }}
          </span>
          <svg
            v-if="resultedActivations.length"
            :class="[
              'size-8 transform transition-transform ml-auto',
              isFormOpen ? 'rotate-180' : 'rotate-0',
            ]"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <div class="w-full" v-show="isFormOpen">
          <div
            class="grid grid-cols-1 grid-rows-3 md:grid-cols-2 md:grid-rows-2 gap-4 mb-6 px-4 lg:px-10 py-6"
          >
            <!-- DATE FROM -->
            <div class="w-full">
              <label
                for="dateFrom"
                class="block text-left font-semibold text-base leading-[21px] mb-4"
              >
                {{ t("labels.date_from") }}
              </label>
              <DatePicker
                id="dateFrom"
                @dateSelected="(newDate: Date) => (dateFrom = newDate)"
                :minDate="new Date('2026-07-09')"
                :maxDate="new Date('2027-10-09')"
              />
            </div>
            <!-- DATE TO -->
            <div class="w-full">
              <label
                for="dateTo"
                class="block text-left font-semibold text-base leading-[21px] mb-4"
              >
                {{ t("labels.date_to") }}
              </label>
              <DatePicker
                id="dateTo"
                @dateSelected="(newDate: Date) => (dateTo = newDate)"
                :minDate="new Date('2026-07-09')"
                :maxDate="new Date('2027-10-09')"
              />
            </div>
            <!-- BIRTHDAY -->
            <!--            <div-->
            <!--              class="w-full"-->
            <!--              v-if="-->
            <!--                userHasSubscriptionWithTier(-->
            <!--                  PREMIUM_CALCULATORS.activations,-->
            <!--                  SUBSCRIPTION_TIERS.consultant-->
            <!--                )-->
            <!--              "-->
            <!--            >-->
            <!--              <label-->
            <!--                for="birthday"-->
            <!--                class="block text-left font-semibold text-base leading-[21px] mb-4"-->
            <!--              >-->
            <!--                {{ t("labels.birthday") }}-->
            <!--              </label>-->
            <!--              <DatePicker-->
            <!--                id="birthday"-->
            <!--                @dateSelected="(newDate: Date) => (birthday = newDate)"-->
            <!--              />-->
            <!--            </div>-->
            <!-- CITY -->
            <div
              class="w-full"
              v-if="
                userHasSubscriptionWithTier(
                  PREMIUM_CALCULATORS.activations,
                  SUBSCRIPTION_TIERS.consultant
                )
              "
            >
              <label
                for="city"
                class="block text-left font-semibold text-base leading-[21px] mb-4"
              >
                {{ t("labels.city") }}
              </label>
              <GooglePlaceAutocompleteInput
                @placeSelected="handlePlaceSelected"
              />
            </div>
            <!-- FAMILY DROPDOWN -->
            <div
              class="w-full"
              v-if="
                userHasSubscriptionWithTier(
                  PREMIUM_CALCULATORS.activations,
                  SUBSCRIPTION_TIERS.family
                ) && familyStore.familyMembers.length > 0
              "
            >
              <AppDropdown
                :label="t('labels.family-members')"
                :items="familyStore.familyMembers"
                :selected-item="selectedFamilyMember"
                :is-dropdown-open="isFamilyMembersDropdownOpen"
                @toggle="
                  isFamilyMembersDropdownOpen = !isFamilyMembersDropdownOpen
                "
              >
                <template #selectedItem="{ item }">
                  <span
                    class="flex justify-between items-center px-4 text-base text-brown"
                  >
                    <span> {{ item?.name }}, {{ item?.city }} </span>
                    <ChevronIcon
                      class="transition-transform"
                      :class="
                        isFamilyMembersDropdownOpen ? 'rotate-180' : 'rotate-0'
                      "
                    />
                  </span>
                </template>
                <template #dropdownItem="{ item }">
                  <div
                    class="flex items-center px-4 text-base text-brown h-10 hover:bg-divider cursor-pointer select-none"
                    @click="() => selectFamilyMember(item)"
                  >
                    {{ item?.name }}, {{ item?.city }}
                  </div>
                </template>
              </AppDropdown>
            </div>
            <!-- FAMILY WITHOUT SELECT -->
            <div
              class="w-full"
              v-if="
                userHasSubscriptionWithTier(
                  PREMIUM_CALCULATORS.activations,
                  SUBSCRIPTION_TIERS.individual
                ) && familyStore.familyMembers.length > 0
              "
            >
              <TextInput
                :label="t('labels.family-members')"
                :model-value="selectedFamilyMember?.name"
                type="text"
                class="pointer-events-none text-left"
              />
            </div>
            <!-- ADD FAMILY MEMBER BUTTON -->
            <div
              class="flex items-center justify-start"
              v-if="
                !userHasSubscriptionWithTier(
                  PREMIUM_CALCULATORS.activations,
                  SUBSCRIPTION_TIERS.consultant
                ) && familyStore.familyMembers.length == 0
              "
            >
              <AddOutlineButton
                btnTextKey="buttons.add-family-member"
                btnWidth="w-[235px]"
                @clicked="handleOpenAddFamilyMemberModal"
              />
            </div>
          </div>
          <!-- CHECKBOXES -->
          <div v-if="hasCheckboxes" class="px-10 pb-6">
            <div class="grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-8">
              <div v-for="key in activationGroup?.activationKeys" :key="key">
                <label class="relative w-full flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    :id="key"
                    :value="key"
                    v-model="selectedActivations"
                    class="peer appearance-none min-w-6 min-h-6 rounded border border-ternary bg-secondary checked:bg-active checked:border-active cursor-pointer"
                  />
                  <CheckIcon
                    class="absolute opacity-0 peer-checked:opacity-100 pointer-events-none ml-1"
                  />
                  <span
                    class="block text-left font-normal text-base leading-100p text-brown ml-2 select-none"
                  >
                    {{ t(`calculators.activations.activation_list.${key}`) }}
                  </span>
                </label>
              </div>
            </div>
          </div>
          <!-- CALCULATE BUTTON -->
          <div class="flex justify-center border-t border-ternary py-6">
            <GradientButton
              class="w-[153px] h-[46px] md:w-[191px] md:h-14"
              bg-color="bg-brown-gradient"
              hover-bg-color="bg-orange-gradient"
              :text="t('buttons.calculate')"
              @click="handleSubmit"
              :is-loading="isLoading"
              :is-disabled="!isFieldsCorrect"
            />
          </div>
        </div>
      </div>

      <ActivationResults
        ref="printableRef"
        v-if="resultedActivations.length"
        :resulted-activations="resultedActivations"
      />
      <div v-if="activationRequested && resultedActivations.length === 0">
        <div class="font-semibold">
          {{ t("calculators.activations.results.no_results") }}
        </div>
      </div>

      <div class="flex gap-4 mb-6" v-if="resultedActivations.length > 0">
        <PrintButton
          btn-classes="size-11"
          icon-classes="mx-auto"
          @click="handlePrint"
        />
        <WordExporter
          :activations="resultedActivations"
          :activationKey="activationGroup?.key"
        />
      </div>

      <!-- FAQs -->
      <div class="mb-6 w-full flex flex-col items-start">
        <div
          class="text-left text-[40px] font-medium leading-100p text-brown mb-6"
        >
          FAQ
        </div>
        <AccordionFaq
          v-if="activationGroup?.key !== undefined"
          :faqTemplateName="`faq.activations.${activationGroup?.key}`"
          :title="t('calculators.activations.faq.activations.title')"
          :collapseButtonText="
            t('calculators.activations.faq.activations.collapse_btn')
          "
        />
        <ActivatorsAccordionFAQ />
        <SectorsAccordionFAQ />
      </div>
    </div>

    <!-- Back Sidebar -->
    <BackSidebar
      v-if="activationGroup"
      :selectedActivation="activationGroup"
      @changeActivationGroup="changeActivation"
    />
    <NotificationComponent />
  </div>
</template>

<style scoped>
::-webkit-scrollbar-track {
  box-shadow: none;
}
</style>
